import React, { useEffect } from 'react';
import cn from 'clsx';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import WidgetItem from '@components-clean/core/widgets/studyDashboard-widget-item';
import { useCountrySelectorContext } from '@contexts-clean/country-selector-context';
import { FieldDefinitionDto } from '@interfaces-clean/field-rdl-definition.interface';
import { useLocalState, useTranslation } from '@hooks/index';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

interface WidgetsProps {
    studyRef: string;
    datasetId: number;
}

const WidgetsSection = ({ studyRef, datasetId }: WidgetsProps) => {
    const { t }: { t: (key: string) => string } = useTranslation('cm');
    const { state } = useLocalState();

    const { country } = useCountrySelectorContext();

    const fieldDefinitionFields = (state?.cm2Fields as FieldDefinitionDto[])?.filter((field) => !field?.isHidden);
    const { data, isLoading, isValidating, error, mutate } = ControlsHubApi.useGetWidgets(
        studyRef,
        datasetId,
        country.value,
    );

    useEffect(() => {
        // eslint-disable-next-line no-void
        void mutate();
    }, [country]);

    const returnWidgetTitle = (fieldKey: string | null | undefined) => {
        const currentFieldDefinition = fieldDefinitionFields?.find(
            (fieldDefinitionField) => fieldDefinitionField.key === fieldKey,
        );

        return currentFieldDefinition?.name;
    };

    const componentClassName = 'widget-section';
    const componentTestId = 'widgets-section';

    if (isLoading || isValidating || error) {
        return (
            <Card
                title={t('studyHubPage.widgets')}
                className={cn(componentClassName, 'empty')}
                data-testid={componentTestId}
                style={{ marginBlock: 6, height: 180 }}
            >
                {error ? <ErrorMessage /> : <Loading />}
            </Card>
        );
    }

    const isEmpty = !data || data.length === 0;

    if (!isEmpty) {
        const content = data?.map((widget) => {
            return (
                <WidgetItem
                    key={widget.id}
                    title={widget.displayName}
                    titleTooltip={returnWidgetTitle(widget.fieldKey)}
                    subtitle={widget.value?.toString() ?? t('componentsClean.widgets.noDataToDisplay')}
                    isDraggable={false}
                    icon={widget.iconName}
                    data-testid={`widget-${widget.id}`}
                    isActiveItem={false}
                    onClose={() => {}}
                />
            );
        });

        return (
            <div className={cn(componentClassName, { empty: isEmpty })} data-testid={componentTestId}>
                {content}
            </div>
        );
    }
};

export default WidgetsSection;
